import { GridColumn } from '@progress/kendo-react-grid'
import { useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { createBreadcrumbWithI18n } from '~/apps/ninesegsAnalyzer/routes/paths'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { StyledGrid } from '~/components/elements/StyledGrid'
import { VStack } from '~/components/elements/VStack'
import { useSurveyForSettingsQueryBrandGroups } from '~/hooks/useSurveyForSettingsQuery'

import { dialogInfoAtom } from '../atoms'
import { useSurveyId } from '../hooks/useSurveyId'
import { pathKey } from '../utils'

import { CommandsCell } from './CommandsCell'
import { Dialogs } from './Dialogs'
import { LinksCell } from './LinksCell'
import { MappingCell } from './MappingCell'
import { NameCell } from './NameCell'

export const BrandGroupList = () => {
  const surveyId = useSurveyId()
  const { t } = useTranslation()

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('settings'),
      createBreadcrumb(pathKey, { surveyId }),
    ],
    [createBreadcrumb, surveyId],
  )

  const brandGroups = useSurveyForSettingsQueryBrandGroups()
  const brandGroupWithIndices = React.useMemo(
    () =>
      brandGroups.map((brandGroup, index) => ({
        ...brandGroup,
        index: index + 1,
      })),
    [brandGroups],
  )

  const setDialogInfo = useSetAtom(dialogInfoAtom)
  const handleAddButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    setDialogInfo({ dialogType: 'create' })
  }, [setDialogInfo])

  return (
    <PageContainer breadcrumbs={breadcrumbs}>
      <VStack align="start" spacing={4} verticalAppearance="stretch">
        <StyledGrid data={brandGroupWithIndices}>
          <GridColumn field="index" title="#" width={32 + 25} />

          <GridColumn cell={NameCell} title={t('brand_group_name')} />

          <GridColumn cell={LinksCell} width={256 + 25} />

          <GridColumn
            cell={MappingCell}
            title={t('mapping_status')}
            width={200 + 25}
          />

          <GridColumn cell={CommandsCell} width={22 + 25} />
        </StyledGrid>

        <StyledButton buttonStyle="success" onClick={handleAddButtonClick}>
          {t('add')}
        </StyledButton>

        <Dialogs />
      </VStack>
    </PageContainer>
  )
}
